import { useCallback, useRef, useState } from "react";

import type { PlaybackTransport } from "../Video";
import type { AnnotationSetValidationIssue, AnnotationStudioController } from "./types";

export function useCapturedFrameActions({
  controller,
  previewFrameId,
  playbackTransport,
  setSelectedFrameId,
  setPreviewFrameId,
  revealFrame,
  requestFrameSeek,
  selectTargetForReview,
}: {
  controller: AnnotationStudioController;
  previewFrameId: string | null;
  playbackTransport: PlaybackTransport;
  setSelectedFrameId: (frameId: string | null) => void;
  setPreviewFrameId: (frameId: string | null) => void;
  revealFrame: (frameId: string) => boolean;
  requestFrameSeek: (seek: {
    sequenceId: string;
    streamId: string;
    timestampSeconds: number;
  }) => void;
  selectTargetForReview: (targetId: string) => void;
}) {
  const [materializingFrameId, setMaterializingFrameId] = useState<string | null>(null);
  const [materializationFailure, setMaterializationFailure] = useState<{
    readonly frameId: string;
    readonly attempt: number;
  } | null>(null);
  const materializationSerialRef = useRef(0);
  const prepareFrame = useCallback(
    (frameId: string, closePreview: boolean) => {
      // Marker creation can resolve and invoke this path before React has committed the controller
      // snapshot that contains the new frame. Read the imperative source of truth here instead of
      // closing over the previous render's draft.
      const frame = controller.getSnapshot().draft?.frames[frameId];
      if (!frame) return null;

      playbackTransport.pause();
      requestFrameSeek({
        sequenceId: frame.sequenceId,
        streamId: frame.streamId,
        timestampSeconds: frame.timestampSeconds,
      });
      setSelectedFrameId(frameId);
      if (closePreview) setPreviewFrameId(null);
      revealFrame(frameId);
      return frame;
    },
    [
      controller,
      playbackTransport,
      requestFrameSeek,
      revealFrame,
      setPreviewFrameId,
      setSelectedFrameId,
    ],
  );

  const synchronizeFrameContext = useCallback(
    (frame: NonNullable<ReturnType<typeof prepareFrame>>) => {
      if (controller.getSnapshot().selectedSequenceId !== frame.sequenceId) {
        controller.selectSequence(frame.sequenceId);
      }
      if (controller.getSnapshot().selectedStreamId !== frame.streamId) {
        controller.selectStream(frame.streamId);
      }
    },
    [controller],
  );

  const materializeFrame = useCallback(
    async (frameId: string) => {
      const existing = controller.getSnapshot().draft?.frames[frameId];
      if (existing) {
        setMaterializationFailure((current) => (current?.frameId === frameId ? null : current));
        return existing;
      }
      if (!controller.materializeAnnotationFrame) return null;
      const serial = ++materializationSerialRef.current;
      setMaterializationFailure((current) => (current?.frameId === frameId ? null : current));
      setMaterializingFrameId(frameId);
      try {
        const materialized = await controller.materializeAnnotationFrame(frameId);
        const frame = controller.getSnapshot().draft?.frames[frameId] ?? null;
        if (!materialized || !frame) {
          if (serial === materializationSerialRef.current) {
            setMaterializationFailure({ frameId, attempt: serial });
          }
          return null;
        }
        if (serial === materializationSerialRef.current) setMaterializationFailure(null);
        return frame;
      } catch {
        if (serial === materializationSerialRef.current) {
          setMaterializationFailure({ frameId, attempt: serial });
        }
        return null;
      } finally {
        if (serial === materializationSerialRef.current) setMaterializingFrameId(null);
      }
    },
    [controller],
  );

  const editMaterializedFrame = useCallback(
    (frameId: string) => {
      const frame = prepareFrame(frameId, previewFrameId === frameId);
      if (!frame) return false;
      if (controller.selectCapturedFrame) {
        controller.selectCapturedFrame(frameId);
      } else {
        synchronizeFrameContext(frame);
      }
      if (
        !controller.selectCapturedFrame &&
        controller.getSnapshot().draft?.activeFrameId === frameId
      ) {
        controller.showCapturedFrame();
      }
      return true;
    },
    [controller, prepareFrame, previewFrameId, synchronizeFrameContext],
  );

  const editFrame = useCallback(
    (frameId: string) => {
      if (editMaterializedFrame(frameId)) return;
      void materializeFrame(frameId).then((frame) => {
        if (frame) editMaterializedFrame(frameId);
      });
    },
    [editMaterializedFrame, materializeFrame],
  );

  const navigateMaterializedFrame = useCallback(
    // A captured-frame marker and the corresponding list row are the same activation intent.
    // Both open the frame's Annotation context regardless of the context they were invoked from.
    (frameId: string) => editMaterializedFrame(frameId),
    [editMaterializedFrame],
  );

  const navigateFrame = useCallback(
    (frameId: string) => {
      if (navigateMaterializedFrame(frameId)) return;
      void materializeFrame(frameId).then((materialized) => {
        if (materialized) navigateMaterializedFrame(frameId);
      });
    },
    [materializeFrame, navigateMaterializedFrame],
  );

  const openReviewFrame = useCallback(
    async (frameId: string, targetId?: string | null): Promise<boolean> => {
      if (!(await materializeFrame(frameId))) return false;
      const currentSnapshot = controller.getSnapshot();
      const currentDraft = currentSnapshot.draft;
      if (
        !currentDraft?.frames[frameId] ||
        !currentDraft.orderedFrameIds.includes(frameId) ||
        (!controller.selectCapturedFrame && currentDraft.activeFrameId !== frameId)
      ) {
        return false;
      }
      if (!editMaterializedFrame(frameId)) return false;
      if (targetId && currentSnapshot.workspace.targets.some((target) => target.id === targetId)) {
        selectTargetForReview(targetId);
      }
      return true;
    },
    [controller, editMaterializedFrame, materializeFrame, selectTargetForReview],
  );

  const openValidationIssue = useCallback(
    async (issue: AnnotationSetValidationIssue, frameId: string): Promise<boolean> => {
      const validation = controller.getSnapshot().annotationSetValidation;
      const currentIssue = validation?.issues.find((candidate) => candidate.id === issue.id);
      if (!currentIssue) return false;
      const locatorBacked = Boolean(
        currentIssue.location || (currentIssue.affectedLocations?.length ?? 0) > 0,
      );
      if (locatorBacked && validation?.status !== "blocked") return false;
      const relatedFrameIds = new Set([
        currentIssue.frameId,
        ...(currentIssue.affectedFrameIds ?? []),
        currentIssue.location?.frameId,
        ...(currentIssue.affectedLocations ?? []).map((location) => location.frameId),
      ]);
      if (!relatedFrameIds.has(frameId)) return false;
      if (!(await materializeFrame(frameId))) return false;
      const latestValidation = controller.getSnapshot().annotationSetValidation;
      const latestIssue = latestValidation?.issues.find((candidate) => candidate.id === issue.id);
      if (!latestIssue || (locatorBacked && latestValidation?.status !== "blocked")) return false;
      const latestRelatedFrameIds = new Set([
        latestIssue.frameId,
        ...(latestIssue.affectedFrameIds ?? []),
        latestIssue.location?.frameId,
        ...(latestIssue.affectedLocations ?? []).map((location) => location.frameId),
      ]);
      if (!latestRelatedFrameIds.has(frameId)) return false;
      return openReviewFrame(frameId, latestIssue.targetId);
    },
    [controller, materializeFrame, openReviewFrame],
  );

  const selectMaterializedFrameForActions = useCallback(
    (frameId: string) => {
      const frame = prepareFrame(frameId, false);
      if (!frame) return false;
      if (controller.selectCapturedFrame) {
        controller.selectCapturedFrame(frameId);
      } else {
        synchronizeFrameContext(frame);
      }
      if (
        !controller.selectCapturedFrame &&
        controller.getSnapshot().draft?.activeFrameId === frameId
      ) {
        controller.showCapturedFrame();
      }
      return true;
    },
    [controller, prepareFrame, synchronizeFrameContext],
  );

  const selectFrameForActions = useCallback(
    (frameId: string) => {
      if (selectMaterializedFrameForActions(frameId)) return;
      void materializeFrame(frameId).then((materialized) => {
        if (materialized) selectMaterializedFrameForActions(frameId);
      });
    },
    [materializeFrame, selectMaterializedFrameForActions],
  );

  const showVideo = useCallback(() => {
    controller.showVideo();
  }, [controller]);

  return {
    editFrame,
    navigateFrame,
    openReviewFrame,
    openValidationIssue,
    selectFrameForActions,
    materializeFrame,
    materializingFrameId,
    materializationFailure,
    showVideo,
  };
}
